/**
 * THE FUNNEL-SESSION TERMINAL LATCH — the bound on the `GET /bff/funnel` storm.
 *
 * ── WHAT WENT WRONG ────────────────────────────────────────────────────────
 * A live QA sweep (2026-08-05) held a session cookie the BFF no longer knew and
 * loaded a funnel page. The storefront fired **767 `GET /bff/funnel` requests in
 * 14.4 seconds** — 30 × `401`, then 737 × `429` once the per-IP baseline limiter
 * kicked in. The same cookie replayed by a single `curl` returns ONE clean `401`.
 * Worse, the recovery call the SPA needs in order to heal itself was `429`'d too,
 * so the customer could not get back in even by reloading, and every other visitor
 * behind the same NAT lost service with them.
 *
 * ── WHY `retry: false` WAS NOT ENOUGH ──────────────────────────────────────
 * `useFunnelSnapshotQuery` has ALWAYS pinned `retry: false`, so this was never a
 * TanStack retry loop — which is exactly why it survived review. The amplifier is
 * that a FAILED read was never REMEMBERED:
 *
 *   - the query holds no data, so TanStack considers it stale forever (`staleTime`
 *     does not apply to a query that has never resolved), and
 *   - it pins `gcTime: 0`, so it is destroyed the instant nothing observes it,
 *
 * which together mean every fresh observer mount issues a brand-new request. Two
 * components observe this key (`FunnelHydrationGate` at the layout root and
 * `CreditPage`), and a session-less funnel page immediately
 * `<Navigate to="/" replace />`s — so observers churn, and each churn re-asks a
 * question the server already answered definitively. Nothing in the client ever
 * concluded "this session is dead; stop asking."
 *
 * ── THE FIX: A 401/403 IS TERMINAL FOR THAT SESSION ────────────────────────
 * `readFunnelSnapshotOnce` records the first terminal failure and thereafter throws
 * the REMEMBERED failure without touching the network. The query still settles into
 * exactly the same error state — so `FunnelHydrationGate` still renders an empty
 * funnel and the funnel steps still send the shopper to the landing chooser, which
 * is the legitimate re-entry path — but it costs ONE request per dead session
 * rather than one per remount.
 *
 * ⚠️ THIS IS NOT A DEBOUNCE. A 767× loop in 14 s is a broken policy, not a timing
 * problem; a debounce would have made the storm slower rather than finite, and it
 * would still have re-asked forever.
 *
 * ── RELEASING THE LATCH ────────────────────────────────────────────────────
 * A terminal latch that never clears would strand a shopper who legitimately starts
 * over. It is released by `establishingFunnelSession`, which wraps the three calls
 * that genuinely MINT or refresh a session — `GET /bff/session`,
 * `POST /bff/address/lookup` and `POST /bff/esiid/lookup`. Nothing else may clear
 * it: quietly re-reading after some unrelated success would re-open the loop.
 *
 * ⚠️ AND IT STILL MUST NOT MINT A SESSION ON A 401. The latch only suppresses a
 * repeat READ. The rule that only the meter sub-flows create a session (so a deep
 * link into the middle of the funnel is not an anonymous entry point — see
 * `bff-client.ts` / `isSessionExpired`) is untouched.
 */

/**
 * The statuses that mean "the funnel session behind this cookie is gone".
 *
 * `401` is the BFF's `session-invalid`. `403` is included because a session the
 * server refuses to act on is just as dead from the client's point of view, and
 * re-asking is just as futile. Deliberately NOT `429`: that one IS temporary, and
 * the right response is to stop hammering (which `retryPolicy` handles), not to
 * declare the session unrecoverable.
 */
const TERMINAL_SESSION_STATUSES: readonly number[] = [401, 403];

/** The remembered failure, boxed so that an `undefined` error is still a record. */
export interface TerminalFunnelFailure {
  readonly error: unknown;
}

export interface FunnelSessionLatch {
  /** The remembered terminal failure, or `undefined` while the session may still be good. */
  terminalFailure(): TerminalFunnelFailure | undefined;
  /** Remember a terminal failure. Non-terminal failures are ignored. */
  record(error: unknown): void;
  /** Forget it — a session was legitimately (re-)established. */
  clear(): void;
}

/**
 * True when a failure means the funnel session is gone.
 *
 * Structural rather than `instanceof BffError` so a service stays free of the API
 * layer, and so a failure that arrives through some other transport is still read
 * correctly. No cast: `in` narrows, `typeof` verifies.
 */
export function isTerminalSessionFailure(error: unknown): boolean {
  if (typeof error !== 'object' || error === null || !('status' in error)) return false;
  const status: unknown = error.status;
  return typeof status === 'number' && TERMINAL_SESSION_STATUSES.includes(status);
}

/**
 * A fresh latch. Exported so every test gets its own and cannot bleed state into
 * the next one; the app uses the `funnelSessionLatch` singleton below, because a
 * browser tab has exactly one funnel session.
 */
export function createFunnelSessionLatch(): FunnelSessionLatch {
  let remembered: TerminalFunnelFailure | undefined;
  return {
    terminalFailure: () => remembered,
    record: (error: unknown) => {
      if (isTerminalSessionFailure(error)) remembered = { error };
    },
    clear: () => {
      remembered = undefined;
    },
  };
}

/** The app-wide latch — one browser tab, one funnel session. */
export const funnelSessionLatch: FunnelSessionLatch = createFunnelSessionLatch();

/**
 * Read the funnel snapshot AT MOST ONCE per dead session.
 *
 * Returns the read's value untouched on success. On a terminal failure it records
 * it and rethrows, so the caller's error handling is unchanged; on every SUBSEQUENT
 * call it rethrows the remembered failure WITHOUT issuing a request. Non-terminal
 * failures (5xx, transport, 429) are rethrown and never latched — those are allowed
 * to be asked again.
 */
export async function readFunnelSnapshotOnce<T>(
  read: () => Promise<T>,
  latch: FunnelSessionLatch = funnelSessionLatch,
): Promise<T> {
  const remembered = latch.terminalFailure();
  if (remembered !== undefined) throw remembered.error;

  try {
    return await read();
  } catch (error) {
    latch.record(error);
    throw error;
  }
}

/**
 * Wrap a call that ESTABLISHES a funnel session, releasing the latch on success.
 *
 * The failure path deliberately does nothing: a failed session mint leaves a dead
 * session dead, so the next snapshot read still short-circuits.
 */
export async function establishingFunnelSession<T>(
  call: () => Promise<T>,
  latch: FunnelSessionLatch = funnelSessionLatch,
): Promise<T> {
  const result = await call();
  latch.clear();
  return result;
}
